import { Box, Button, Popover, Typography } from '@mui/material';
import {
  ArrowSquareOut,
  BookOpen,
  Copy,
  FileText,
  Link as LinkIcon,
  PencilSimpleLine,
  Trash,
} from '@phosphor-icons/react';
import React from 'react';
import { useAgentsSidebarContext } from './context';

interface ActionMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onRename?: () => void;
  onShare?: () => void;
  onOpenInNewTab?: () => void;
  onCreateInterface?: () => void;
  onAddToLibrary?: () => void;
  onDuplicate?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  showEdit?: boolean;
  libraryLabel?: string;
}

export const ActionMenu: React.FC<ActionMenuProps> = ({
  anchorEl,
  onClose,
  onRename,
  onShare,
  onOpenInNewTab,
  onCreateInterface,
  onAddToLibrary,
  onDuplicate,
  onEdit,
  onDelete,
  showEdit = false,
  libraryLabel = 'Add to Library',
}) => {
  const { isDarkMode, palette } = useAgentsSidebarContext();

  const buttonSx = {
    width: '100%',
    background: 'unset',
    justifyContent: 'flex-start',
    textTransform: 'unset' as const,
    fontSize: '16px',
    boxShadow: 'unset',
    px: '8px',
    gap: '8px',
    color: isDarkMode ? palette.gray[300] : palette.gray[600],
    '&:hover': {
      background: isDarkMode ? palette.gray[800] : palette.gray[200],
      boxShadow: 'unset',
    },
  };

  const iconColor = isDarkMode ? palette.gray[400] : palette.gray[500];
  const textColor = isDarkMode ? palette.gray[300] : palette.gray[600];

  const renderAction = (
    icon: React.ReactNode,
    label: string,
    onClick: () => void,
    customSx?: Record<string, unknown>,
  ) => (
    <Button
      variant='contained'
      onClick={() => {
        onClose();
        onClick();
      }}
      sx={{ ...buttonSx, ...customSx }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        {icon}
        <Typography sx={{ color: customSx?.color || textColor }}>
          {label}
        </Typography>
      </Box>
    </Button>
  );

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: { marginLeft: '8px' },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: '8px',
          background: isDarkMode ? palette.gray[900] : '#FFFFFF',
          minWidth: '180px',
        }}
      >
        {onRename &&
          renderAction(
            <PencilSimpleLine size={20} color={iconColor} />,
            'Rename',
            onRename,
          )}
        {onShare &&
          renderAction(
            <LinkIcon size={20} color={iconColor} />,
            'Share',
            onShare,
          )}
        {onOpenInNewTab &&
          renderAction(
            <ArrowSquareOut size={20} color={iconColor} />,
            'Open in new tab',
            onOpenInNewTab,
          )}
        {onCreateInterface &&
          renderAction(
            <FileText size={20} color={iconColor} />,
            'Create Interface',
            onCreateInterface,
          )}
        {onAddToLibrary &&
          renderAction(
            <BookOpen size={20} color={iconColor} />,
            libraryLabel,
            onAddToLibrary,
          )}
        {onDuplicate &&
          renderAction(
            <Copy size={20} color={iconColor} />,
            'Duplicate',
            onDuplicate,
          )}
        {showEdit &&
          onEdit &&
          renderAction(
            <PencilSimpleLine size={20} color={iconColor} />,
            'Edit',
            onEdit,
          )}
        {onDelete &&
          renderAction(
            <Trash size={20} color='#b91c1c' />,
            'Delete',
            onDelete,
            {
              color: '#b91c1c',
              '&:hover': {
                background: isDarkMode ? '#FFADB7' : '#fecdd3',
                boxShadow: 'unset',
              },
            },
          )}
      </Box>
    </Popover>
  );
};
